import { MessageType, useAi } from '@/context/ai'
import { useAuth } from '@/context/auth'
import { useAuthPrompt } from '@/context/authPrompt'
import { getConfig } from '@/lib/config'
import { getTzName } from '@/lib/datetime'
import InputBox from '@/widgets/v2/AI/Chat/InputBox'
import { ChatMessage, MarkdownRenderer } from '@/widgets/v2/AI/Chat/Message'
import Prompts from '@/widgets/v2/AI/Chat/Prompts'
import SkeletonMessage from '@/widgets/AI/Chat/SkeletonMessage'
import Thread from '@/widgets/v2/AI/Chat/Thread'
import Sidebar from '@/widgets/v2/AI/Sidebar'
import { WebPlatform } from '@getsetup-io/event-producer'
import { getTime } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import * as styles from './style'
import { useAnonymousChat } from '@/context/anonymousChatContext'
import IcoMoon from '@/components/IcoMoon'

interface ClubProperty {
  name: string
  iid: string
  description?: string
  city?: string
  clubImage?: string
  email?: string
  passion?: string
  siteUrl?: string
  state?: string
  terms?: string
  zip?: string
  uuid?: string
}

interface ClubItem {
  metadata: Record<string, any>
  properties: ClubProperty
  uuid: string
  vectors: Record<string, any>
}

interface ClubCollection {
  objects: ClubItem[]
}

export interface ClubsAIResponse {
  results: ClubCollection
}

const platform = WebPlatform({
  cookieDomain: getConfig().publicRuntimeConfig?.events?.cookieDomain,
})

interface Props {
  authToken?: string
  partnerId?: string
  sandboxMode?: boolean
}

const Chat = (props: Props): JSX.Element => {
  const config = getConfig()

  // Get appropriate partnerId and socketUrl based on sandboxMode prop
  const defaultPartnerId = props.sandboxMode
    ? config.publicRuntimeConfig.orchestrationServiceSandbox.defaultPartnerId
    : config.publicRuntimeConfig.defaultPartnerId

  const {
    state: { user, tenantId },
  } = useAuth()
  const {
    state: { query: searchQuery, messageType },
  } = useAi()
  const { toggleAuthPrompt } = useAuthPrompt()
  const { anonymousMessageLimitReached, incrementMessageCount } = useAnonymousChat()

  // Get socketUrl based directly on sandboxMode prop
  const socketUrl = props.sandboxMode
    ? config.publicRuntimeConfig.orchestrationServiceSandbox.url
    : config.publicRuntimeConfig.orchestrationService.url

  const socketRef = useRef<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isMessagesLoaded, setIsMessagesLoaded] = useState(false)

  const [deltaMessages, setDeltaMessages] = useState<string[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [prompts, setPrompts] = useState<string[]>([])
  const [showPrompts, setShowPrompts] = useState<boolean>(true)
  const [currentPrompts, setCurrentPrompts] = useState<string[]>([])
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const toggleSidebar = (): void => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const chatThreadRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current.removeAllListeners()
    }

    console.log(`Initializing socket connection to: ${socketUrl}`)
    socketRef.current = io(socketUrl, { transports: ['websocket'] })
    setIsConnected(false)
    setIsMessagesLoaded(false)

    socketRef.current.on('connect', async () => {
      console.log('***** Connected to chat server *****', socketUrl)
      const deviceId = await platform.getDeviceId()
      const roomId = `${props.partnerId || defaultPartnerId}:${props.sandboxMode ? 'helen-sandbox' : 'helen'}:${
        user?.id || deviceId
      }`

      // Get the user's timezone using getTzName
      const timezone = getTzName()

      console.log('***** Socket emitting auth', { roomId, timezone })
      if (socketRef.current) {
        if (user?.id) {
          socketRef.current.emit('auth', {
            token: props.authToken,
            room: roomId,
            timezone: timezone, // Send timezone during authentication
            tenant: tenantId,
          })
        } else {
          socketRef.current.emit('anonymousAuth', {
            userId: deviceId,
            room: `anonymous:${roomId}`,
            timezone: timezone, // Send timezone during authentication
            tenant: tenantId,
          })
        }
      }
    })

    socketRef.current.on('authed', async () => {
      setIsConnected(true)
    })

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current.removeAllListeners()
        socketRef.current = null
      }
    }
  }, [socketUrl, props.authToken, props.partnerId, defaultPartnerId, user?.id, props.sandboxMode])

  useEffect(() => {
    if (!isConnected || !socketRef.current) return

    socketRef.current.emit('plsBootstrapChat', {
      embeddingId: defaultPartnerId,
    })

    socketRef.current
      .on('chatBootstrap', (messages: ChatMessage[]) => {
        setMessages(
          messages.map((message) => {
            return { ...message, type: 'USER' }
          }),
        )
        setIsMessagesLoaded(true)
      })
      .on('newMessage', (message: ChatMessage) => {
        setDeltaMessages([])
        setMessages((previous) => [...previous, { ...message, type: 'USER' }])
      })
      .on('showBotTyping', (message: ChatMessage & { username: string }) => {
        const botTypingMessage: ChatMessage = {
          id: `BOT_TYPING-${message.username}`,
          type: 'BOT_TYPING',
          name: message.username,
          content: 'typing...',
          timestamp: getTime(new Date()),
          userId: 'ai-response',
          hidden: false,
        }
        setShowPrompts(false)
        setTimeout(() => {
          setMessages((previous) => [...previous, botTypingMessage])
        }, 500)
        setDeltaMessages([])
      })
      .on('hideBotTyping', () => {
        setMessages((previous) => [...previous.filter((message) => message.type !== 'BOT_TYPING')])
      })
      .on('chatPrompts', (prompts: string[]) => {
        setPrompts(prompts)
      })
      .on('dmx', (delta: string) => {
        setDeltaMessages((previous) => [...previous, delta])
      })

    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners('chatBootstrap')
        socketRef.current.removeAllListeners('newMessage')
        socketRef.current.removeAllListeners('showBotTyping')
        socketRef.current.removeAllListeners('hideBotTyping')
        socketRef.current.removeAllListeners('chatPrompts')
        socketRef.current.removeAllListeners('dmx')
      }
    }
  }, [isConnected, defaultPartnerId])

  useEffect(() => {
    if (prompts.length > 0 && currentPrompts.toString() !== prompts.toString()) {
      setCurrentPrompts(prompts)
      setShowPrompts(true)
    }
  }, [prompts, currentPrompts])

  useEffect(() => {
    if (!isConnected || !isMessagesLoaded || !searchQuery || !socketRef.current) return
    if (!user?.id) {
      if (anonymousMessageLimitReached) {
        toggleAuthPrompt()
        return
      }
      incrementMessageCount()
    }

    socketRef.current.emit('message', {
      content: searchQuery,
      thumbnail64: '',
      type: messageType,
    })
    setMessages((prevMessages) => [...prevMessages.filter((msg) => msg.name)])
    setShowPrompts(false)
  }, [isConnected, isMessagesLoaded, searchQuery, messageType])

  useEffect(() => {
    if (deltaMessages.length > 0 && messages.at(-1)?.type === 'BOT_TYPING') {
      setMessages((previous) => [...previous.filter((message) => message.type !== 'BOT_TYPING')])
    }
  }, [deltaMessages, messages])

  async function sendPrompt(prompt: string): Promise<void> {
    if (!socketRef.current) return
    if (!user?.id) {
      if (anonymousMessageLimitReached) {
        toggleAuthPrompt()
        return
      }
      incrementMessageCount()
    }

    socketRef.current.emit('message', {
      content: prompt,
      thumbnail64: '',
      type: MessageType.UserDynamicPrompt,
    })
    setMessages((prevMessages) => [...prevMessages.filter((msg) => msg.name)])
    setShowPrompts(false)
  }
  return (
    <main className={`p-4 sm:px-6 lg:px-10 xxl:px-16`}>
      <div className={`w-full max-w-[1600px] m-auto p-0`}>
        <div className={`w-full p-0 mx-auto xxl:p-6`}>
          <div className="p-0 lg:overflow-auto">
            {
              <>
                <div ref={chatThreadRef} className={styles.messageBox}>
                  {!isConnected && (
                    <div className="flex flex-col gap-4">
                      <SkeletonMessage />
                      <SkeletonMessage />
                      <SkeletonMessage />
                      <SkeletonMessage />
                      <SkeletonMessage />
                    </div>
                  )}
                  <Thread messages={messages} isHelenSandbox={props.sandboxMode} />
                  {deltaMessages.length > 0 && (
                    <MarkdownRenderer>{deltaMessages.map((delta) => delta).join('')}</MarkdownRenderer>
                  )}
                  {showPrompts && prompts.length ? <Prompts prompts={prompts} onSubmit={sendPrompt} /> : null}
                </div>
                <InputBox />
                <div className={`${styles.moreButtonContainer} flex justify-end`}>
                  <button
                    onClick={toggleSidebar}
                    className={`z-[3] min-h-12 min-w-28 flex items-center justify-center py-2 px-4 text-white rounded-full bg-primary`}
                  >
                    <IcoMoon icon="grid-dots" size={30} className="mr-1" />
                    <span className="text-center text-base font-semibold">More</span>
                  </button>
                </div>
              </>
            }
          </div>
          <div className="lg:ml-4 overflow-auto">
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
          </div>
        </div>
      </div>
    </main>
  )
}

export default Chat
